import { useEffect, useState } from "react";

const YEARS = [2050, ...Array.from({ length: 11 }, (_, i) => 2026 + i * 2)];

export const START = Date.UTC(2025, 11, 31, 22, 0, 0); // 2026-01-01 00:00 GMT+2 (SAST)
export const END = Date.UTC(2049, 11, 31, 22, 0, 0); // 2050-01-01 00:00 GMT+2 (SAST)

export const localTimeParts = (now) => {
  const d = new Date(now);
  return {
    hh: String(d.getHours()).padStart(2, "0"),
    mm: String(d.getMinutes()).padStart(2, "0"),
    ss: String(d.getSeconds()).padStart(2, "0"),
  };
};

export const tzLabel = () => {
  try {
    const short = new Date()
      .toLocaleTimeString("en-US", { timeZoneName: "short" })
      .split(" ")
      .pop();
    const iana = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    return { short, iana };
  } catch {
    return { short: "", iana: "" };
  }
};

const NetZeroClock = () => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const progress = Math.min(Math.max((now - START) / (END - START), 0), 1);
  const handAngle = 30 + progress * 330;

  const remaining = Math.max(END - now, 0);
  const days = Math.floor(remaining / 86400000);

  const { hh, mm, ss } = localTimeParts(now);
  const tz = tzLabel();

  return (
    <div data-testid="netzero-clock" className="relative w-72 h-72 sm:w-[26rem] sm:h-[26rem] select-none">
      <div className="absolute inset-0 rounded-full border-4 border-lime bg-carbon shadow-[0_0_100px_rgba(132,204,22,0.25)]" />
      <div className="absolute inset-4 rounded-full border-[3px] border-cyber shadow-[inset_0_0_40px_rgba(6,182,212,0.15)]" />

      {YEARS.map((y, i) => {
        const angle = i * 30;
        return (
          <div key={y} className="absolute inset-0" style={{ transform: `rotate(${angle}deg)` }}>
            <span
              data-testid={`clock-year-${y}`}
              className={`flash-year absolute left-1/2 top-5 sm:top-7 font-mono font-bold ${
                y === 2050
                  ? "text-lime text-lg sm:text-2xl"
                  : "text-gray-300 text-sm sm:text-lg"
              }`}
              style={{
                transform: `translateX(-50%) rotate(${-angle}deg)`,
                animationDelay: `${i * 0.2}s`,
              }}
            >
              {y}
            </span>
            <span className="absolute left-1/2 top-14 sm:top-[4.5rem] h-2.5 w-0.5 bg-cyber/60" style={{ transform: "translateX(-50%)" }} />
          </div>
        );
      })}

      <div
        data-testid="clock-hand"
        className="absolute left-1/2 top-1/2 w-1 rounded-full bg-lime shadow-[0_0_16px_rgba(132,204,22,0.9)]"
        style={{
          height: "32%",
          transformOrigin: "bottom center",
          transform: `translate(-50%, -100%) rotate(${handAngle}deg)`,
        }}
      />

      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-obsidian border-2 border-lime/40 px-7 py-6 sm:px-9 sm:py-8 text-center shadow-[0_0_40px_rgba(132,204,22,0.15)]">
        <p className="font-mono text-[9px] sm:text-[10px] uppercase tracking-[0.25em] text-lime">Net Zero 2050</p>
        <p data-testid="clock-current-time" className="mt-1.5 font-mono font-semibold text-white text-2xl sm:text-4xl tracking-wider leading-none whitespace-nowrap">
          {hh}:{mm}:{ss}
        </p>
        <p data-testid="clock-timezone" className="mt-1 font-mono text-[8px] sm:text-[9px] uppercase tracking-[0.2em] text-gray-500">
          {tz.short}{tz.iana ? ` · ${tz.iana.replace("_", " ")}` : ""}
        </p>
        <p data-testid="clock-countdown" className="mt-2 font-display font-extrabold text-white text-lg sm:text-2xl whitespace-nowrap leading-none">
          {days.toLocaleString()}
          <span className="text-lime text-xs sm:text-base align-top"> days left</span>
        </p>
      </div>
    </div>
  );
};

export default NetZeroClock;
